import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  Box,
  Typography,
  AccordionDetails,
  CircularProgress,
  Stack,
  useTheme,
  ClickAwayListener,
  Tooltip,
  IconButton
} from "@mui/material";
import { ErrorPanel, Warning20Px } from "@qubit/autoparts";

import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectWorkstationAutostoreGridId } from "~/redux/selectors/workstationsSelectors";

import { useAvailableBinReportQuery } from "~/redux/warehouse/autostoreGrid.hooks";

type CompartmentCount = 1 | 2 | 4;

export function BinQuantityWidget() {
  const [expanded, setExpanded] = useState(false);
  const [tooltipIds, setTooltipIds] = useState<string[]>([]);
  const { t } = useTranslation();
  const trackedPageName = useMixpanelPageName();
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const {
    data: availableBinReport,
    isLoading,
    error
  } = useAvailableBinReportQuery(
    autostoreGridId && expanded ? autostoreGridId : skipToken
  );

  const palette = useTheme().palette;
  return (
    <Box py={4} minWidth="245px">
      <Accordion
        expanded={expanded}
        onChange={() => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: expanded
              ? "Collapse Bin Quantity Widget"
              : "Expand Bin Quantity Widget"
          });
          setExpanded(!expanded);
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "secondary.dark",
            borderRadius: expanded ? "10px 10px 0 0" : "10px",
            transition: "border-radius"
          }}
        >
          <Typography variant="button" whiteSpace="nowrap">
            {t("estimated available bin counts")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: "flex", flexDirection: "column" }}>
          {isLoading && (
            <CircularProgress sx={{ alignSelf: "center" }} size={24} />
          )}
          {error && <ErrorPanel message={getMessageFromRtkError(error)} />}
          {!isLoading && !error && !availableBinReport?.length && (
            <Typography variant="body1">{t("no results")}</Typography>
          )}
          {(availableBinReport ?? [])
            .toSorted((a, b) => a.numberOfCompartments - b.numberOfCompartments)
            .map((br) => (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                key={br.binConfigurationId}
              >
                <Typography variant="body1" key={br.binConfigurationId}>
                  {t(
                    `autostoreBin.${br.numberOfCompartments as CompartmentCount} compartments`
                  )}
                  : {br.binsAvailable}
                </Typography>
                {br.binsAvailable < 50 && (
                  <ClickAwayListener
                    onClickAway={() =>
                      setTooltipIds(
                        tooltipIds.filter((id) => id !== br.binConfigurationId)
                      )
                    }
                  >
                    <Tooltip
                      disableHoverListener
                      onClose={() => setTooltipIds([])}
                      open={tooltipIds.includes(br.binConfigurationId)}
                      title={t("low bin count")}
                      placement="right"
                    >
                      <IconButton
                        sx={{ py: 0 }}
                        onClick={() =>
                          setTooltipIds([...tooltipIds, br.binConfigurationId])
                        }
                        aria-label={`${br.binConfigurationId} low bin count`}
                      >
                        <Warning20Px fill={palette.warning.dark} />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                )}
              </Stack>
            ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
